/**
 * Workflow Builder — Full 3-panel visual canvas editor
 *
 * Layout:
 * ┌──────────┬──────────────────────────────────────┬──────────────┐
 * | Node     |           React Flow Canvas          | Properties   |
 * | Palette  |                                      |   Panel      |
 * | (180px)  |    Nodes + Edges + Minimap           |   (320px)    |
 * └──────────┴──────────────────────────────────────┴──────────────┘
 *
 * Features:
 * - Drag nodes from palette onto canvas
 * - Click node to edit properties in right panel
 * - Test run with animated status propagation
 * - Save workflow (mock)
 * - Undo/Redo with history stack (Ctrl+Z / Ctrl+Y)
 * - Keyboard shortcuts (Delete, Copy, Paste, Select All, Save)
 * - Save state indicator with unsaved changes guard
 * - Copy/Paste nodes with edge duplication
 * - Multi-select with box selection and selection badge
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  ReactFlow,
  Controls,
  MiniMap,
  Background,
  BackgroundVariant,
  useNodesState,
  useEdgesState,
  addEdge,
  type Node,
  type Edge,
  type Connection,
  type NodeTypes,
  Panel,
  SelectionMode,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  Save, Play, ArrowLeft, Loader2, Workflow, Map,
  Check, AlertTriangle, Brain, Undo2, Redo2, Clipboard, Circle, LayoutGrid,
  MoreVertical, Keyboard,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTheme } from '@/components/theme-provider';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  getNodeRuntimeSupport,
  NODE_TYPE_CONFIG,
  WORKFLOW_STATUS_STYLES,
  type NodeType,
  type WorkflowNode,
  type WorkflowEdge,
} from '@/lib/mock-automate-data';
import {
  useWorkflow,
  useExecuteWorkflow,
  useUpdateWorkflowLifecycle,
  useExecutionDetail,
  type ExecutionDetail,
  type ExecutionStepRow,
} from '@/lib/use-automate';
import {
  useWorkflowDefinition,
  useUpdateWorkflowDefinition,
  serverNodesToRF,
  serverEdgesToRF,
  rfToSavePayload,
} from '@/lib/use-workflow-definition';
import { uuidv4 } from '@/lib/uuid';
import { NODE_TYPES } from '@/components/workflow/workflow-nodes';
import { NodePalette } from '@/components/workflow/node-palette';
import { NodePropertyPanel } from '@/components/workflow/node-property-panel';
import { SupervisorPanel } from '@/components/workflow/supervisor-panel';
import { AgentConfigSheet } from '@/components/workflow/agent-config-sheet';
import { QualityScorecard, type ScorecardData, type StepResult, type CriterionResult } from '@/components/workflow/quality-scorecard';

// ── Route ────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/automate/workflows/$id')({
  component: WorkflowBuilderPage,
});

// ── nodeTypes MUST be stable (module-level) to prevent re-mounts ─────────

const nodeTypes: NodeTypes = NODE_TYPES as unknown as NodeTypes;

function stepTokenCount(output: unknown): number {
  if (!output || typeof output !== 'object') return 0;
  const value = (output as { tokenCount?: unknown }).tokenCount;
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

// ── Convert mock data to React Flow format ───────────────────────────────

function toRFNodes(nodes: WorkflowNode[]): Node[] {
  return nodes.map((n) => ({
    id: n.id,
    type: n.type,
    position: n.position,
    data: { label: n.data.label, config: n.data.config, status: 'idle' as const },
  }));
}

function toRFEdges(edges: WorkflowEdge[]): Edge[] {
  return edges.map((e) => ({
    id: e.id,
    source: e.source,
    target: e.target,
    sourceHandle: e.sourceHandle,
    label: e.label,
    type: 'smoothstep',
    animated: e.animated ?? true,
    style: { strokeWidth: 1.5 },
    labelStyle: { fontSize: 10 },
  }));
}

// ── Builder page component ───────────────────────────────────────────────

function WorkflowBuilderPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { mode } = useTheme();
  const isDark = mode === 'dark';
  const reactFlowWrapper = useRef<HTMLDivElement>(null);

  // Real-data: workflow metadata (header) joined from automations + workflow_definitions.
  // BUG-630 — surface the load state + error explicitly so a random/expired UUID
  // renders a clear not-found instead of an editable-looking "New Workflow"
  // shell. Pre-fix, missing rows silently resolved to `workflow=null` and the
  // builder still rendered, letting operators believe they were editing a real
  // workflow that didn't exist (AUTO-ROUTE-006).
  const workflowQuery = useWorkflow(id);
  const { data: workflowReal, isLoading: workflowLoading, isError: workflowErrored } = workflowQuery;
  const workflow = workflowReal ?? null;
  const workflowMissing = !workflowLoading && (workflowErrored || workflow === null);

  // Real-data: A1 workflow_definitions. Returns null on 404 (treat
  // as "no real row yet" — canvas still seeds from mock).
  const { data: realDef } = useWorkflowDefinition(id);
  const updateDef = useUpdateWorkflowDefinition(id);

  const [nodes, setNodes, onNodesChange] = useNodesState(
    workflow ? toRFNodes(workflow.nodes) : ([] as Node[]),
  );
  const [edges, setEdges, onEdgesChange] = useEdgesState(
    workflow ? toRFEdges(workflow.edges) : ([] as Edge[]),
  );

  // Hydrate canvas from real workflow_definitions row when one exists.
  // Runs once after the GET resolves to a non-null payload.
  const hasHydratedRealDef = useRef(false);
  useEffect(() => {
    if (realDef && !hasHydratedRealDef.current) {
      hasHydratedRealDef.current = true;
      setNodes(serverNodesToRF(realDef.nodes));
      setEdges(serverEdgesToRF(realDef.edges));
    }
  }, [realDef, setNodes, setEdges]);

  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [testRunning, setTestRunning] = useState(false);
  const [showMinimap, setShowMinimap] = useState(true);
  const [workflowName, setWorkflowName] = useState(workflow?.name ?? 'New Workflow');
  const [editingName, setEditingName] = useState(false);
  const [rightPanelMode, setRightPanelMode] = useState<'properties' | 'supervisor'>('supervisor');
  const [agentSheetNodeId, setAgentSheetNodeId] = useState<string | null>(null);
  const [showShortcuts, setShowShortcuts] = useState(false);

  // Step-through execution state
  const [stepMode, setStepMode] = useState(false);
  const [stepExecutionOrder, setStepExecutionOrder] = useState<string[]>([]);
  const [stepCurrentIndex, setStepCurrentIndex] = useState(0);
  const [scorecardData, setScorecardData] = useState<ScorecardData | null>(null);
  // #197 / #205 — remember the executionId that produced the current
  // scorecard so the QualityScorecard can deep-link to /history/:id.
  const [scorecardExecId, setScorecardExecId] = useState<string | null>(null);

  // ── Undo/Redo history ──────────────────────────────────────────────
  const MAX_HISTORY = 50;
  const [history, setHistory] = useState<Array<{ nodes: Node[]; edges: Edge[] }>>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const isRestoringRef = useRef(false);

  const pushHistory = useCallback((currentNodes: Node[], currentEdges: Edge[]) => {
    if (isRestoringRef.current) return;
    setHistory((prev) => {
      const truncated = prev.slice(0, historyIndex + 1);
      const next = [...truncated, {
        nodes: JSON.parse(JSON.stringify(currentNodes)),
        edges: JSON.parse(JSON.stringify(currentEdges)),
      }];
      if (next.length > MAX_HISTORY) next.shift();
      return next;
    });
    setHistoryIndex((prev) => Math.min(prev + 1, MAX_HISTORY - 1));
  }, [historyIndex]);

  const undo = useCallback(() => {
    if (historyIndex <= 0) return;
    const prevIndex = historyIndex - 1;
    const snapshot = history[prevIndex];
    if (!snapshot) return;
    isRestoringRef.current = true;
    setNodes(snapshot.nodes);
    setEdges(snapshot.edges);
    setHistoryIndex(prevIndex);
    requestAnimationFrame(() => { isRestoringRef.current = false; });
  }, [history, historyIndex, setNodes, setEdges]);

  const redo = useCallback(() => {
    if (historyIndex >= history.length - 1) return;
    const nextIndex = historyIndex + 1;
    const snapshot = history[nextIndex];
    if (!snapshot) return;
    isRestoringRef.current = true;
    setNodes(snapshot.nodes);
    setEdges(snapshot.edges);
    setHistoryIndex(nextIndex);
    requestAnimationFrame(() => { isRestoringRef.current = false; });
  }, [history, historyIndex, setNodes, setEdges]);

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  // Push initial state on mount
  useEffect(() => {
    if (history.length === 0 && nodes.length > 0) {
      pushHistory(nodes, edges);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Dirty / save state tracking ────────────────────────────────────
  const [isDirty, setIsDirty] = useState(false);
  const [changeCount, setChangeCount] = useState(0);

  const markDirty = useCallback(() => {
    setIsDirty(true);
    setChangeCount((c) => c + 1);
  }, []);

  // beforeunload guard
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // ── Clipboard + multi-select state ─────────────────────────────────
  const [clipboardNodes, setClipboardNodes] = useState<Node[]>([]);
  const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>([]);

  // Debounce timer for node move history push
  const movePushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Supervisor state
  const [wfPurpose, setWfPurpose] = useState(workflow?.purpose ?? '');
  const [wfCriteria, setWfCriteria] = useState(workflow?.successCriteria ?? []);
  const [wfConstraints, setWfConstraints] = useState(workflow?.constraints ?? []);
  const [wfSupervisorMode, setWfSupervisorMode] = useState(workflow?.supervisorMode ?? 'standard');

  // Active polling target — declared up here so #170's hydration
  // effect below can clear it on workflow-id change.
  const [activeExecutionId, setActiveExecutionId] = useState<string | null>(null);

  // #170 — useState above only runs at FIRST mount. The `workflow`
  // query is async, so on the first render it's still undefined and
  // every header field stays at its fallback ("New Workflow", empty
  // purpose, []). When the query resolves OR the user navigates to a
  // different workflow id without unmounting, none of these fields
  // re-sync. Force a reset whenever the route id or the loaded workflow
  // identity changes.
  useEffect(() => {
    if (!workflow) return;
    setWorkflowName(workflow.name ?? 'Untitled workflow');
    setWfPurpose(workflow.purpose ?? '');
    setWfCriteria(workflow.successCriteria ?? []);
    setWfConstraints(workflow.constraints ?? []);
    setWfSupervisorMode(workflow.supervisorMode ?? 'standard');
    // Clear ephemeral builder state — anything tied to a previous
    // workflow's canvas should not leak across navigation.
    setIsDirty(false);
    setChangeCount(0);
    setSelectedNodeId(null);
    setClipboardNodes([]);
    setSelectedNodeIds([]);
    setHistory([]);
    setHistoryIndex(-1);
    setScorecardData(null);
    setScorecardExecId(null);
    setActiveExecutionId(null);
    hasHydratedRealDef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, workflow?.id]);

  const selectedNode = useMemo(
    () => nodes.find((n) => n.id === selectedNodeId) ?? null,
    [nodes, selectedNodeId],
  );

  const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const isLiveWorkflow = UUID_REGEX.test(id);
  const workflowStatus = workflow?.status ?? 'draft';
  const workflowIsEnabled = workflow?.isEnabled ?? workflowStatus === 'active';
  const workflowCanExecute =
    isLiveWorkflow && workflowStatus === 'active' && workflowIsEnabled && !isDirty;
  const ss = WORKFLOW_STATUS_STYLES[workflowStatus];

  // ── Connect nodes ──────────────────────────────────────────────────

  // Connection validation — prevent invalid wiring
  const isValidConnection = useCallback((connection: Edge | Connection) => {
    // Prevent self-connections
    if (connection.source === connection.target) return false;
    // Prevent duplicate connections between same source/target handles
    const exists = edges.some(e =>
      e.source === connection.source && e.target === connection.target &&
      e.sourceHandle === connection.sourceHandle && e.targetHandle === connection.targetHandle
    );
    if (exists) return false;
    // Prevent connecting output to output or input to input (React Flow handles this but be safe)
    return true;
  }, [edges]);

  const onConnect = useCallback(
    (connection: Connection) => {
      setEdges((eds) => {
        const newEdges = addEdge(
          {
            ...connection,
            // #168 — give the edge a real UUID up-front. addEdge would
            // otherwise auto-assign `xy-edge__<source>-<target>` which
            // is rejected by the backend EdgeInputSchema.
            id: uuidv4(),
            type: 'default',
            animated: false,
            style: { strokeWidth: 1.5, stroke: 'hsl(var(--muted-foreground) / 0.3)' },
            labelStyle: { fontSize: 10 },
          },
          eds,
        );
        // Push history after edge connect
        setTimeout(() => {
          setNodes((currentNodes) => {
            pushHistory(currentNodes, newEdges);
            return currentNodes;
          });
        }, 0);
        return newEdges;
      });
      markDirty();
    },
    [setEdges, setNodes, pushHistory, markDirty],
  );

  // ── Wrapped onNodesChange with move debounce ──────────────────────

  const handleNodesChange = useCallback(
    (changes: Parameters<typeof onNodesChange>[0]) => {
      onNodesChange(changes);

      // Check for position changes (node move)
      const hasPosition = changes.some((c: any) => c.type === 'position' && c.position);
      // Check for node removal
      const hasRemove = changes.some((c: any) => c.type === 'remove');
      // Check for selection changes
      const hasSelect = changes.some((c: any) => c.type === 'select');

      if (hasSelect) {
        // Update multi-select tracking from React Flow's internal state
        setTimeout(() => {
          setNodes((currentNodes) => {
            const selected = currentNodes.filter((n) => n.selected).map((n) => n.id);
            setSelectedNodeIds(selected);
            return currentNodes;
          });
        }, 0);
      }

      if (hasPosition) {
        markDirty();
        // Debounce history push for moves (fires many events during drag)
        if (movePushTimerRef.current) clearTimeout(movePushTimerRef.current);
        movePushTimerRef.current = setTimeout(() => {
          setNodes((currentNodes) => {
            setEdges((currentEdges) => {
              pushHistory(currentNodes, currentEdges);
              return currentEdges;
            });
            return currentNodes;
          });
        }, 300);
      }

      if (hasRemove) {
        markDirty();
        setTimeout(() => {
          setNodes((currentNodes) => {
            setEdges((currentEdges) => {
              pushHistory(currentNodes, currentEdges);
              return currentEdges;
            });
            return currentNodes;
          });
        }, 0);
      }
    },
    [onNodesChange, setNodes, setEdges, pushHistory, markDirty],
  );

  // ── Wrapped onEdgesChange ─────────────────────────────────────────

  const handleEdgesChange = useCallback(
    (changes: Parameters<typeof onEdgesChange>[0]) => {
      onEdgesChange(changes);
      const hasRemove = changes.some((c: any) => c.type === 'remove');
      if (hasRemove) {
        markDirty();
        setTimeout(() => {
          setNodes((currentNodes) => {
            setEdges((currentEdges) => {
              pushHistory(currentNodes, currentEdges);
              return currentEdges;
            });
            return currentNodes;
          });
        }, 0);
      }
    },
    [onEdgesChange, setNodes, setEdges, pushHistory, markDirty],
  );

  // ── Node click ─────────────────────────────────────────────────────

  const onNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
    // Agent nodes open the full-width config sheet instead of the narrow panel
    if (node.type === 'agent') {
      setAgentSheetNodeId(node.id);
      return;
    }
    setSelectedNodeId(node.id);
    setRightPanelMode('properties');
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNodeId(null);
  }, []);

  // ── Drop handler (from palette drag) ──────────────────────────────

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  // Helper: append a node at the given canvas position, push history,
  // mark dirty, and select it. Used by both drag-and-drop (drop point)
  // and click-to-add (canvas centre).
  const addNodeAt = useCallback(
    (type: NodeType, position: { x: number; y: number }) => {
      if (!NODE_TYPE_CONFIG[type]) return;
      const support = getNodeRuntimeSupport(type);
      if (support.status === 'unsupported') {
        toast.error('Node not wired for V1 runtime', {
          description: `${NODE_TYPE_CONFIG[type].label} cannot be added until its production runtime is implemented.`,
        });
        return;
      }
      const config = NODE_TYPE_CONFIG[type];
      const newNode: Node = {
        // #168 — must be a real UUID, not `<type>-<timestamp>-<rand>`.
        // Edges reference this id and the backend EdgeInputSchema
        // requires UUID source/target. Generating it here means the
        // first save round-trips correctly.
        id: uuidv4(),
        type,
        position,
        selected: true,
        data: {
          label: config.label,
          config: {},
          status: 'idle',
        },
      };
      setNodes((nds) => {
        const cleared: Node[] = nds.map((n) => ({ ...n, selected: false }));
        const updated: Node[] = [...cleared, newNode];
        setEdges((currentEdges) => {
          pushHistory(updated, currentEdges);
          return currentEdges;
        });
        return updated;
      });
      setSelectedNodeId(newNode.id);
      setRightPanelMode('properties');
      markDirty();
    },
    [setNodes, setEdges, pushHistory, markDirty],
  );

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const type = e.dataTransfer.getData('application/reactflow-type') as NodeType;
      if (!type || !NODE_TYPE_CONFIG[type]) return;

      const bounds = reactFlowWrapper.current?.getBoundingClientRect();
      if (!bounds) return;

      addNodeAt(type, {
        x: e.clientX - bounds.left - 100,
        y: e.clientY - bounds.top - 35,
      });
    },
    [addNodeAt],
  );

  // Click-to-add (#F1): drop the node near the visible canvas centre so
  // the user actually sees it appear. We can't read the React Flow
  // viewport transform from outside the instance without lifting state,
  // so we use the wrapper midpoint as a stable, predictable target —
  // good enough; users can drag to fine-tune.
  const handlePaletteAdd = useCallback(
    (type: NodeType) => {
      const bounds = reactFlowWrapper.current?.getBoundingClientRect();
      // Distinct stagger so consecutive clicks don't pile up exactly.
      const stagger = (nodes.length % 6) * 30;
      const position = bounds
        ? { x: bounds.width / 2 - 100 + stagger, y: bounds.height / 2 - 40 + stagger }
        : { x: 200 + stagger, y: 200 + stagger };
      addNodeAt(type, position);
      toast.info(`Added ${NODE_TYPE_CONFIG[type].label} node`, {
        description: 'Drag to reposition or use the property panel to configure.',
      });
    },
    [addNodeAt, nodes.length],
  );

  // ── Copy/Paste ────────────────────────────────────────────────────

  const copySelectedNodes = useCallback(() => {
    const selected = nodes.filter((n) => n.selected);
    if (selected.length === 0 && selectedNodeId) {
      const single = nodes.find((n) => n.id === selectedNodeId);
      if (single) {
        setClipboardNodes([JSON.parse(JSON.stringify(single))]);
        toast.info('Copied 1 node');
        return;
      }
    }
    if (selected.length > 0) {
      setClipboardNodes(JSON.parse(JSON.stringify(selected)));
      toast.info(`Copied ${selected.length} node${selected.length > 1 ? 's' : ''}`);
    }
  }, [nodes, selectedNodeId]);

  const pasteNodes = useCallback(() => {
    if (clipboardNodes.length === 0) return;

    const idMap: Record<string, string> = {};
    const newNodes: Node[] = clipboardNodes.map((n) => {
      // #168 — pasted nodes also need real UUIDs so edges save.
      const newId = uuidv4();
      idMap[n.id] = newId;
      return {
        ...n,
        id: newId,
        position: { x: n.position.x + 50, y: n.position.y + 50 },
        selected: false,
        data: {
          ...n.data,
          label: `${(n.data as any).label} (copy)`,
        },
      };
    });

    // Duplicate edges between copied nodes
    const copiedIds = new Set(clipboardNodes.map((n) => n.id));
    const newEdges: Edge[] = edges
      .filter((e) => copiedIds.has(e.source) && copiedIds.has(e.target))
      .map((e) => ({
        ...e,
        id: uuidv4(),
        source: idMap[e.source] ?? e.source,
        target: idMap[e.target] ?? e.target,
      }));

    setNodes((nds) => {
      const updated = [...nds, ...newNodes];
      setEdges((eds) => {
        const updatedEdges = [...eds, ...newEdges];
        pushHistory(updated, updatedEdges);
        return updatedEdges;
      });
      return updated;
    });
    markDirty();
    toast.info(`Pasted ${newNodes.length} node${newNodes.length > 1 ? 's' : ''}`);
  }, [clipboardNodes, edges, setNodes, setEdges, pushHistory, markDirty]);

  // ── Delete selected nodes ─────────────────────────────────────────

  const deleteSelectedNodes = useCallback(() => {
    const selected = nodes.filter((n) => n.selected);
    const toDelete = selected.length > 0
      ? new Set(selected.map((n) => n.id))
      : selectedNodeId ? new Set([selectedNodeId]) : new Set<string>();

    if (toDelete.size === 0) return;

    setNodes((nds) => {
      const updated = nds.filter((n) => !toDelete.has(n.id));
      setEdges((eds) => {
        const updatedEdges = eds.filter((e) => !toDelete.has(e.source) && !toDelete.has(e.target));
        pushHistory(updated, updatedEdges);
        return updatedEdges;
      });
      return updated;
    });
    setSelectedNodeId(null);
    setSelectedNodeIds([]);
    markDirty();
    toast.info(`Deleted ${toDelete.size} node${toDelete.size > 1 ? 's' : ''}`);
  }, [nodes, selectedNodeId, setNodes, setEdges, pushHistory, markDirty]);

  // ── Select all nodes ──────────────────────────────────────────────

  const selectAllNodes = useCallback(() => {
    setNodes((nds) => {
      const updated = nds.map((n) => ({ ...n, selected: true }));
      setSelectedNodeIds(updated.map((n) => n.id));
      return updated;
    });
  }, [setNodes]);

  // ── Deselect all ──────────────────────────────────────────────────

  const deselectAll = useCallback(() => {
    setNodes((nds) => nds.map((n) => ({ ...n, selected: false })));
    setSelectedNodeId(null);
    setSelectedNodeIds([]);
    setRightPanelMode('supervisor');
  }, [setNodes]);

  // ── Tidy / Auto-layout (DAG top-to-bottom) ─────────────────────────

  const handleTidy = useCallback(() => {
    if (nodes.length === 0) return;

    // Build adjacency list from edges using plain objects
    const children: Record<string, string[]> = {};
    const parentMap: Record<string, string[]> = {};
    for (const e of edges) {
      if (!children[e.source]) children[e.source] = [];
      children[e.source].push(e.target);
      if (!parentMap[e.target]) parentMap[e.target] = [];
      parentMap[e.target].push(e.source);
    }

    // Find root nodes (no incoming edges)
    const roots = nodes.filter((n) => !(parentMap[n.id]?.length));
    const startNodes = roots.length > 0 ? roots : nodes;

    // BFS to assign levels
    const levels: Record<string, number> = {};
    const queue: { id: string; level: number }[] = startNodes.map((n) => ({ id: n.id, level: 0 }));
    const visited: Record<string, boolean> = {};

    while (queue.length > 0) {
      const { id: qId, level } = queue.shift()!;
      if (visited[qId]) {
        if ((levels[qId] ?? 0) < level) levels[qId] = level;
        continue;
      }
      visited[qId] = true;
      levels[qId] = level;
      for (const childId of children[qId] ?? []) {
        queue.push({ id: childId, level: level + 1 });
      }
    }

    // Handle disconnected nodes
    const levelValues = Object.values(levels);
    const maxLevel = levelValues.length > 0 ? Math.max(...levelValues) : 0;
    let disconnectedLevel = maxLevel + 1;
    for (const n of nodes) {
      if (levels[n.id] === undefined) {
        levels[n.id] = disconnectedLevel;
        disconnectedLevel++;
      }
    }

    // Group nodes by level
    const levelGroups: Record<number, string[]> = {};
    for (const [nodeId, level] of Object.entries(levels)) {
      const lvl = level as number;
      if (!levelGroups[lvl]) levelGroups[lvl] = [];
      levelGroups[lvl].push(nodeId);
    }

    // Calculate new positions
    const Y_GAP = 150;
    const X_GAP = 250;
    const newPositions: Record<string, { x: number; y: number }> = {};

    for (const [levelStr, nodeIds] of Object.entries(levelGroups)) {
      const level = Number(levelStr);
      const count = nodeIds.length;
      const totalWidth = (count - 1) * X_GAP;
      const startX = -totalWidth / 2;

      nodeIds.forEach((nodeId: string, i: number) => {
        newPositions[nodeId] = {
          x: startX + i * X_GAP,
          y: level * Y_GAP,
        };
      });
    }

    // Apply new positions to nodes
    setNodes((nds) => {
      const updated = nds.map((n) => {
        const pos = newPositions[n.id];
        return pos ? { ...n, position: pos } : n;
      });
      setEdges((currentEdges) => {
        pushHistory(updated, currentEdges);
        return currentEdges;
      });
      return updated;
    });
    markDirty();

    toast.success('Layout tidied', {
      description: `${nodes.length} nodes arranged across ${Object.keys(levelGroups).length} levels`,
    });
  }, [nodes, edges, setNodes, setEdges, pushHistory, markDirty]);

  // ── Save ───────────────────────────────────────────────────────────
  // Real save: PUT to A1's /workflow-definitions/:automationId when
  // the route id is a UUID (live automation). Mock ids fall back to
  // the original simulated save so demo workflows keep working.

  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

  const handleSave = useCallback(() => {
    setSaving(true);
    const agentsMissingInstructions = nodes.filter((n) => {
      if (n.type !== 'agent') return false;
      const cfg = (n.data as { config?: Record<string, unknown> }).config ?? {};
      const instr =
        (typeof cfg.prompt === 'string' ? cfg.prompt : '') ||
        (typeof cfg.instructions === 'string' ? cfg.instructions : '');
      return instr.trim().length === 0;
    });
    if (agentsMissingInstructions.length > 0) {
      const names = agentsMissingInstructions
        .map((n) => (n.data as { label?: string }).label ?? 'Agent')
        .slice(0, 3)
        .join(', ');
      toast.warning('Agent node missing instructions', {
        description:
          `${agentsMissingInstructions.length} agent node` +
          `${agentsMissingInstructions.length === 1 ? '' : 's'} (${names}${agentsMissingInstructions.length > 3 ? ', …' : ''}) ` +
          `${agentsMissingInstructions.length === 1 ? 'has' : 'have'} no instructions and will fail at run time. ` +
          `Add a task in the node's Instructions field.`,
      });
    }
    const routeIsUuid = UUID_RE.test(id);
    if (!routeIsUuid) {
      // Demo / mock workflow — preserve original UX.
      setTimeout(() => {
        setSaving(false);
        setIsDirty(false);
        setChangeCount(0);
        toast.success('Workflow saved', {
          description: `${nodes.length} nodes, ${edges.length} connections`,
        });
      }, 600);
      return;
    }
    // #238 — pass purpose / criteria / constraints / supervisorMode so
    // they round-trip through the save. The earlier helper signature
    // dropped them silently; every save wiped them in DB.
    const payload = rfToSavePayload(
      workflowName || workflow?.name || 'Untitled workflow',
      nodes,
      edges,
      {
        purpose: wfPurpose,
        supervisorMode: wfSupervisorMode,
        successCriteria: wfCriteria,
        constraints: wfConstraints,
      },
    );
    updateDef.mutate(payload, {
      onSuccess: () => {
        setSaving(false);
        setIsDirty(false);
        setChangeCount(0);
        toast.success('Workflow saved', {
          description: `${nodes.length} nodes, ${edges.length} connections`,
        });
      },
      onError: (err: unknown) => {
        setSaving(false);
        const msg = (err as { message?: string })?.message ?? 'Save failed';
        toast.error('Save failed', { description: msg });
      },
    });
  }, [id, nodes, edges, workflow?.name, workflowName, wfPurpose, wfSupervisorMode, wfCriteria, wfConstraints, updateDef]);

  // ── Keyboard shortcuts ────────────────────────────────────────────

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const isMod = e.metaKey || e.ctrlKey;
      const target = e.target as HTMLElement;
      // Don't capture when typing in inputs
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;

      // Delete / Backspace — delete selected nodes
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        deleteSelectedNodes();
        return;
      }

      // Ctrl+Z — Undo
      if (isMod && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
        return;
      }

      // Ctrl+Y or Ctrl+Shift+Z — Redo
      if ((isMod && e.key === 'y') || (isMod && e.key === 'z' && e.shiftKey)) {
        e.preventDefault();
        redo();
        return;
      }

      // Ctrl+S — Save
      if (isMod && e.key === 's') {
        e.preventDefault();
        handleSave();
        return;
      }

      // Ctrl+C — Copy
      if (isMod && e.key === 'c') {
        e.preventDefault();
        copySelectedNodes();
        return;
      }

      // Ctrl+V — Paste
      if (isMod && e.key === 'v') {
        e.preventDefault();
        pasteNodes();
        return;
      }

      // Ctrl+A — Select all
      if (isMod && e.key === 'a') {
        e.preventDefault();
        selectAllNodes();
        return;
      }

      // Escape — deselect all, close property panel
      if (e.key === 'Escape') {
        e.preventDefault();
        deselectAll();
        setScorecardData(null);
        setAgentSheetNodeId(null);
        return;
      }

      // ? — show keyboard shortcuts
      if (e.key === '?' && !isMod) {
        setShowShortcuts(true);
        return;
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [deleteSelectedNodes, undo, redo, handleSave, copySelectedNodes, pasteNodes, selectAllNodes, deselectAll]);

  // ── Real Test Run — calls POST /automate-workflows/:id/execute and
  // polls GET /executions/:execId. Replaces the old simulated path
  // (random delays + Math.random()-driven outcomes). The supervisor
  // is auto-derived server-side from the saved workflow definition.

  const executeMutation = useExecuteWorkflow();
  const lifecycleMutation = useUpdateWorkflowLifecycle();
  const queryClient = useQueryClient();
  // activeExecutionId state declared earlier (#170 hydration effect needs to clear it).
  const { data: executionDetail } = useExecutionDetail(id, activeExecutionId);

  // #197 / #205 — track which execution IDs we've already finalized
  // so the terminal-state branch fires its toasts + scorecard build
  // EXACTLY ONCE per execution. Without this guard, the effect below
  // re-runs whenever react-query's cached `executionDetail` object is
  // re-emitted (e.g. another query invalidation, re-render from
  // surrounding state, route refocus), and the user sees ghost
  // "Test run completed" toasts for runs that finished minutes ago.
  const finalizedExecRef = useRef<string | null>(null);

  const handleLifecycleAction = useCallback(async () => {
    if (!isLiveWorkflow) {
      toast.error('Demo workflow', {
        description: 'Lifecycle actions only apply to saved workflows.',
      });
      return;
    }
    if (isDirty) {
      toast.error('Save first', {
        description: 'Lifecycle actions apply to the saved workflow definition. Save your changes, then update its status.',
      });
      return;
    }
    if (workflowStatus === 'archived') {
      toast.error('Archived workflow', {
        description: 'Restore archived workflows from the workflow list before running.',
      });
      return;
    }

    const activating = workflowStatus !== 'active' || !workflowIsEnabled;
    try {
      await lifecycleMutation.mutateAsync({
        workflowId: id,
        status: activating ? 'active' : 'paused',
        isEnabled: activating,
      });
      toast.success(activating ? 'Workflow activated' : 'Workflow paused', {
        description: activating
          ? 'Test Run is now available for this workflow.'
          : 'This workflow will not run until it is activated again.',
      });
      await workflowQuery.refetch();
    } catch (err) {
      toast.error('Could not update workflow', {
        description: err instanceof Error ? err.message : 'Server returned an error.',
      });
    }
  }, [
    id,
    isDirty,
    isLiveWorkflow,
    lifecycleMutation,
    workflowIsEnabled,
    workflowQuery,
    workflowStatus,
  ]);

  const handleTestRun = useCallback(async () => {
    if (testRunning || nodes.length === 0) return;
    if (isDirty) {
      toast.error('Save first', {
        description: 'Test Run executes the saved workflow definition. Save your changes, then run.',
      });
      return;
    }
    if (!isLiveWorkflow) {
      toast.error('Demo workflow', {
        description: 'Test Run only works on real workflows (created via New Workflow). Demo workflows are read-only previews.',
      });
      return;
    }
    if (workflowStatus !== 'active' || !workflowIsEnabled) {
      toast.error('Activate workflow first', {
        description: 'Draft, paused, and disabled workflows cannot be executed.',
      });
      return;
    }
    setTestRunning(true);
    setScorecardData(null);
    setScorecardExecId(null);
    // #197 / #205 — clear the "finalized" guard so the next terminal
    // state of the upcoming execution actually fires the scorecard +
    // toast (the guard otherwise dedupes within an execution id, but
    // back-to-back test runs share workflowId so we reset on every
    // fresh kick-off).
    finalizedExecRef.current = null;
    setNodes((nds) => nds.map((n) => ({ ...n, data: { ...n.data, status: 'idle' } })));
    try {
      const result = await executeMutation.mutateAsync({ workflowId: id });
      setActiveExecutionId(result.executionId);
      toast.info('Workflow execution started', {
        description: 'The AI supervisor will validate each step against the workflow\'s purpose + criteria.',
      });
    } catch (err) {
      setTestRunning(false);
      toast.error('Could not start workflow', {
        description: err instanceof Error ? err.message : 'Server returned an error.',
      });
    }
  }, [
    id,
    isLiveWorkflow,
    isDirty,
    testRunning,
    nodes.length,
    executeMutation,
    setNodes,
    workflowIsEnabled,
    workflowStatus,
  ]);

  // Apply real per-step status to the canvas as polling fetches updates.
  // Map step status → node status the visual nodes already understand.
  useEffect(() => {
    if (!executionDetail) return;
    const stepByNodeId: Record<string, ExecutionStepRow> = {};
    for (const s of executionDetail.steps) {
      if (s.workflowNodeId) stepByNodeId[s.workflowNodeId] = s;
    }
    setNodes((nds) =>
      nds.map((n, idx) => {
        const step = stepByNodeId[n.id];
        const stepStatus = step?.status.toLowerCase();
        const nodeStatus =
          !step
            ? 'idle'
            : stepStatus === 'success' ||
              stepStatus === 'completed' ||
              stepStatus === 'approved'
            ? 'success'
            : stepStatus === 'failed' ||
              stepStatus === 'failure' ||
              stepStatus === 'rejected' ||
              stepStatus === 'denied'
            ? 'error'
            : stepStatus === 'suspended'
            ? 'success'
            : 'running';
        // Build a real inspect-badge payload from the actual step
        // output so hovering a finished node shows its real I/O.
        //
        // #197 / #205 — do NOT fabricate `{_real: true, ok: true}` when
        // the runner persisted no output. The badge must reflect what
        // actually happened: empty output is rendered as `null`, and
        // the UI's badge component shows "(no output)" for that case.
        // Lying with a synthetic `ok: true` was the same defect class
        // as the audit-fail-open issue — looks successful while
        // hiding that the runner produced nothing.
        const inspectData = step
          ? {
              status: (stepStatus === 'success' ||
                stepStatus === 'completed' ||
                stepStatus === 'approved'
                ? 'success'
                : stepStatus === 'failed' ||
                  stepStatus === 'failure' ||
                  stepStatus === 'rejected' ||
                  stepStatus === 'denied'
                ? 'error'
                : 'partial') as 'success' | 'error' | 'partial',
              durationMs: step.durationMs ?? 0,
              input: { nodeKind: step.nodeKind ?? n.type, label: step.label ?? '' },
              output: (step.output as Record<string, unknown> | null) ?? null,
            }
          : undefined;
        return {
          ...n,
          data: {
            ...n.data,
            status: nodeStatus,
            inspectData,
            inspectStaggerIndex: idx,
          },
        };
      }),
    );

    const executionStatus = executionDetail.status.toLowerCase();
    if (
      [
        'success',
        'succeeded',
        'completed',
        'failure',
        'failed',
        'suspended',
        'cancelled',
      ].includes(executionStatus)
    ) {
      // #197 / #205 — only run the terminal-state branch ONCE per
      // execution id. The effect re-fires whenever react-query
      // re-emits the cached executionDetail; without this guard we
      // re-build the scorecard, re-fire the success/failure toast,
      // and re-invalidate queries on every render — visible to users
      // as ghost toasts and flickering scorecard mounts.
      if (finalizedExecRef.current === executionDetail.id) return;
      finalizedExecRef.current = executionDetail.id;
      // Build the scorecard from real step results + supervisor verdicts
      // embedded inside step.output._supervisor.
      const stepResults: StepResult[] = executionDetail.steps.map((s) => {
        const supervisor = (s.output as { _supervisor?: { result?: string; feedback?: string } } | null)?._supervisor;
        const supervisorResult: 'pass' | 'partial' | 'fail' | 'not_evaluated' =
          supervisor?.result === 'pass' || supervisor?.result === 'partial' || supervisor?.result === 'fail'
            ? supervisor.result
            : 'not_evaluated';
        return {
          nodeId: s.workflowNodeId ?? s.id,
          nodeName: s.label ?? s.nodeKind ?? 'Step',
          nodeType: s.nodeKind ?? 'unknown',
          status:
            ['success', 'completed', 'approved'].includes(s.status.toLowerCase())
              ? 'success'
              : ['failed', 'failure', 'rejected', 'denied'].includes(
                  s.status.toLowerCase(),
                )
              ? 'error'
              : 'success',
          durationMs: s.durationMs ?? 0,
          supervisorResult,
          supervisorFeedback: supervisor?.feedback ?? (s.error ?? undefined),
          retryCount: 0,
        };
      });
      const totalDuration =
        executionDetail.durationMs ??
        stepResults.reduce((acc, s) => acc + s.durationMs, 0);
      const hasError = ['failure', 'failed', 'cancelled'].includes(executionStatus);
      // Derive criterionResults purely from supervisor verdicts.
      const criterionResults: CriterionResult[] = wfCriteria.map((c) => {
        // Find any step whose supervisor matched this criterion. Without
        // that linkage in the API yet, fall back to overall pass/fail.
        const stepHit = stepResults.find((s) => s.supervisorResult === 'fail');
        const stepPartial = stepResults.find((s) => s.supervisorResult === 'partial');
        const overall = hasError || stepHit ? 'fail' : stepPartial ? 'partial' : 'pass';
        return {
          criterionId: c.id,
          description: c.description,
          result: overall,
          evidence: hasError
            ? 'Workflow run failed before this criterion could be fully validated.'
            : 'Verdict aggregated from per-step supervisor checks.',
          weight: c.weight,
        };
      });
      const overall: 'pass' | 'partial' | 'fail' =
        hasError ? 'fail'
        : criterionResults.length === 0 ? 'partial'
        : criterionResults.some((c) => c.result === 'fail' && c.weight === 'critical') ? 'fail'
        : criterionResults.some((c) => c.result !== 'pass') ? 'partial'
        : 'pass';
      setScorecardData({
        overall,
        criterionResults,
        stepResults,
        totalDurationMs: totalDuration,
        supervisorMetrics: {
          totalValidations: stepResults.filter((s) => s.supervisorResult !== 'not_evaluated').length,
          totalRetries: 0,
          supervisorTokens: executionDetail.steps.reduce(
            (sum, step) => sum + stepTokenCount(step.output),
            0,
          ),
        },
      });
      setScorecardExecId(executionDetail.id);
      setTestRunning(false);
      if (['success', 'succeeded', 'completed'].includes(executionStatus)) {
        toast.success('Test run completed', {
          description: `${stepResults.length} step${stepResults.length === 1 ? '' : 's'} · ${overall.toUpperCase()}`,
        });
      } else if (executionStatus === 'suspended') {
        toast.info('Workflow suspended at approval gate', {
          description: 'Awaiting human approval before continuing.',
        });
      } else {
        toast.error('Test run failed', {
          description: executionDetail.error ?? 'See step trace for details.',
        });
      }
      // #200 — refresh History + workflow run-stats so the new
      // execution row + updated success_rate appear without a
      // hard reload.
      void queryClient.invalidateQueries({ queryKey: ['automate-executions'] });
      void queryClient.invalidateQueries({ queryKey: ['automate-workflows'] });
      // Drop polling now that we're done.
      setActiveExecutionId(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executionDetail, wfCriteria]);

  // ── Step-through execution ──────────────────────────────────────────

  const startStepThrough = useCallback(() => {
    if (nodes.length === 0) return;
    // Build execution order (same BFS as test run)
    const triggerIds = nodes.filter((n) => n.type === 'trigger').map((n) => n.id);
    const visited: Record<string, boolean> = {};
    const queue = [...triggerIds];
    const order: string[] = [];
    while (queue.length > 0) {
      const current = queue.shift()!;
      if (visited[current]) continue;
      visited[current] = true;
      order.push(current);
      for (const e of edges) {
        if (e.source === current && !visited[e.target]) queue.push(e.target);
      }
    }
    if (order.length === 0) order.push(...nodes.map(n => n.id));

    setStepExecutionOrder(order);
    setStepCurrentIndex(0);
    setStepMode(true);
    setScorecardData(null);

    // Reset all nodes and highlight first
    setNodes(nds => nds.map(n => ({
      ...n, data: { ...n.data, status: n.id === order[0] ? 'running' : 'idle' }
    })));
    toast.info('Step-through mode', { description: `Step 1 of ${order.length} — click "Step" to execute` });
  }, [nodes, edges, setNodes]);

  const executeStep = useCallback(() => {
    // Bug #714 — step-through previously used Math.random() to decide
    // success vs error for the current node, with no runner-backed
    // evidence (no execution row, no workflow_step_executions, no
    // scorecard). The controls have been removed from the toolbar; this
    // function is kept as a no-op so any latent keyboard shortcut or
    // ref doesn't crash. Real runner-backed step-through is a follow-up.
    if (!stepMode || stepCurrentIndex >= stepExecutionOrder.length) return;
    // Walk the order silently without fabricating outcomes.
    const nextIndex = stepCurrentIndex + 1;
    if (nextIndex >= stepExecutionOrder.length) {
      setStepMode(false);
      return;
    }
    setStepCurrentIndex(nextIndex);
  }, [stepMode, stepCurrentIndex, stepExecutionOrder]);

  const stopStepThrough = useCallback(() => {
    setStepMode(false);
    setStepCurrentIndex(0);
    setStepExecutionOrder([]);
    setNodes(nds => nds.map(n => ({ ...n, data: { ...n.data, status: 'idle' } })));
    toast.info('Step-through cancelled');
  }, [setNodes]);

  // ── Property panel handlers ────────────────────────────────────────

  const handleNodeUpdate = useCallback(
    (nodeId: string, label: string, config: Record<string, unknown>) => {
      setNodes((nds) => {
        const updated = nds.map((n) =>
          n.id === nodeId
            ? { ...n, data: { ...n.data, label, config } }
            : n,
        );
        setEdges((currentEdges) => {
          pushHistory(updated, currentEdges);
          return currentEdges;
        });
        return updated;
      });
      markDirty();
      toast.info('Node updated');
    },
    [setNodes, setEdges, pushHistory, markDirty],
  );

  const handleNodeDelete = useCallback(
    (nodeId: string) => {
      setNodes((nds) => {
        const updated = nds.filter((n) => n.id !== nodeId);
        setEdges((eds) => {
          const updatedEdges = eds.filter((e) => e.source !== nodeId && e.target !== nodeId);
          pushHistory(updated, updatedEdges);
          return updatedEdges;
        });
        return updated;
      });
      setSelectedNodeId(null);
      markDirty();
      toast.info('Node deleted');
    },
    [setNodes, setEdges, pushHistory, markDirty],
  );

  // ── Render ─────────────────────────────────────────────────────────

  // BUG-630 (AUTO-ROUTE-006) — workflow load resolved to missing/error.
  // Render a clear not-found state with a Back-to-workflows link instead
  // of the editable-looking builder shell. Loading state is allowed to
  // pass through so the canvas does not flash an error before the query
  // resolves.
  if (workflowMissing) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-6">
        <AlertTriangle className="h-12 w-12 text-muted-foreground/40 mb-4" />
        <h2 className="text-lg font-semibold">Workflow not found</h2>
        <p className="text-sm text-muted-foreground mt-1 max-w-md">
          The workflow you requested does not exist in this tenant, or you do not have access to it.
        </p>
        <Button
          variant="outline"
          size="sm"
          className="mt-4 h-8 text-xs gap-1.5"
          onClick={() => navigate({ to: '/automate', search: {} } as any)}
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to workflows
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header toolbar */}
      <div className="flex items-center justify-between px-3 py-2 border-b bg-background z-10">
        <div className="flex items-center gap-3 min-w-0">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2"
            onClick={() => navigate({ to: '/automate', search: {} } as any)}
            title="Back to workflows"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>

          {/* Editable workflow name with save state dot */}
          <div className="flex items-center gap-1.5">
            <Tooltip>
              <TooltipTrigger asChild>
                <Circle
                  className={cn(
                    'h-2.5 w-2.5 shrink-0 fill-current',
                    isDirty ? 'text-orange-500' : 'text-emerald-500',
                  )}
                />
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">
                {isDirty ? `${changeCount} unsaved change${changeCount !== 1 ? 's' : ''}` : 'All changes saved'}
              </TooltipContent>
            </Tooltip>

            {editingName ? (
              <Input
                className="h-7 text-sm font-bold w-64"
                value={workflowName}
                onChange={(e) => { setWorkflowName(e.target.value); markDirty(); }}
                onBlur={() => setEditingName(false)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') setEditingName(false);
                }}
                autoFocus
              />
            ) : (
              <button
                className="text-sm font-bold hover:text-primary transition-colors truncate max-w-[300px]"
                onClick={() => setEditingName(true)}
                title="Click to rename"
              >
                {workflowName}
              </button>
            )}
          </div>

          <Badge variant="outline" className={cn('text-[10px] h-5', ss.color)}>
            {ss.label}
          </Badge>

          <span className="text-[10px] text-muted-foreground">
            {nodes.length} nodes / {edges.length} edges
          </span>

          {isDirty && (
            <span className="text-[10px] text-orange-500 font-medium">
              {changeCount} unsaved
            </span>
          )}
        </div>

        <div className="flex items-center gap-1">
          {/* Undo */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2"
                onClick={undo}
                disabled={!canUndo}
                title="Undo (Ctrl+Z)"
              >
                <Undo2 className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">Undo (Ctrl+Z)</TooltipContent>
          </Tooltip>

          {/* Redo */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2"
                onClick={redo}
                disabled={!canRedo}
                title="Redo (Ctrl+Y)"
              >
                <Redo2 className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">Redo (Ctrl+Y)</TooltipContent>
          </Tooltip>

          <div className="w-px h-5 bg-border mx-1" />

          {/* Tidy layout */}
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2"
            onClick={handleTidy}
            disabled={nodes.length === 0}
            title="Auto-arrange nodes (tidy layout)"
          >
            <LayoutGrid className="h-3.5 w-3.5" />
          </Button>

          {/* Minimap toggle */}
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2"
            onClick={() => setShowMinimap((v) => !v)}
            title={showMinimap ? 'Hide minimap' : 'Show minimap'}
          >
            <Map className="h-3.5 w-3.5" />
          </Button>

          {/* Clipboard indicator */}
          {clipboardNodes.length > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-1 px-1.5 text-muted-foreground">
                  <Clipboard className="h-3 w-3" />
                  <span className="text-[10px]">{clipboardNodes.length}</span>
                </div>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">
                {clipboardNodes.length} node{clipboardNodes.length !== 1 ? 's' : ''} in clipboard (Ctrl+V to paste)
              </TooltipContent>
            </Tooltip>
          )}

          <div className="w-px h-5 bg-border mx-1" />

          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs gap-1.5"
            onClick={handleSave}
            disabled={saving}
            title="Save workflow (Ctrl+S)"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            {saving ? 'Saving...' : 'Save'}
          </Button>

          {/* Automate audit #3 — Publish button (draft) / Enabled toggle (published). See worktree for full JSX. */}
          {/* More actions dropdown — lifecycle, delete, shortcuts */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" className="h-8 w-8" title="More actions">
                <MoreVertical className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {workflowStatus !== 'archived' ? (
                <DropdownMenuItem
                  onClick={handleLifecycleAction}
                  disabled={lifecycleMutation.isPending || !isLiveWorkflow}
                  className="text-xs"
                >
                  {workflowStatus === 'active' && workflowIsEnabled ? (
                    <>
                      <Circle className="h-3.5 w-3.5 mr-2" /> Pause workflow
                    </>
                  ) : (
                    <>
                      <Check className="h-3.5 w-3.5 mr-2" /> Activate workflow
                    </>
                  )}
                </DropdownMenuItem>
              ) : null}
              {/* Bug #721 — Import/Export menu items removed. Export
                  previously serialized raw node data (including secret
                  refs) to a downloaded JSON file with no redaction.
                  Import accepted arbitrary JSON in the browser and
                  applied it directly to canvas state with no backend
                  schema validation, no tenant-ownership check, no
                  size/depth limit, and no permission/secret review.
                  Both paths are hidden until the backend
                  validation/redaction endpoint lands (versioned
                  schema, signed bundle, redacted export). The
                  Keyboard Shortcuts entry remains available below. */}
              <DropdownMenuItem onClick={() => setShowShortcuts(true)} className="text-xs">
                <Keyboard className="h-3.5 w-3.5 mr-2" /> Keyboard Shortcuts
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={!workflowCanExecute ? 0 : -1} className="inline-flex">
                <Button
                  size="sm"
                  className="h-8 text-xs gap-1.5"
                  onClick={handleTestRun}
                  disabled={testRunning || nodes.length === 0 || !workflowCanExecute}
                  title={
                    !workflowCanExecute
                      ? 'Activate and save this workflow before running a test execution'
                      : 'Run test execution'
                  }
                >
                  {testRunning ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Play className="h-3.5 w-3.5" />
                  )}
                  {testRunning ? 'Running...' : 'Test Run'}
                </Button>
              </span>
            </TooltipTrigger>
            {!workflowCanExecute && (
              <TooltipContent side="bottom" className="text-xs">
                Activate this workflow (⋮ menu → Activate workflow) and save your changes before running a test execution.
              </TooltipContent>
            )}
          </Tooltip>

          {/* Bug #714 — Step-through controls hidden. The previous
              implementation simulated execution locally with Math.random
              and no runner/history evidence (no execution row, no
              workflow_step_executions, no scorecard). Hiding the
              affordance prevents operators from mistaking the simulation
              for a real run. Real step-through requires runner
              instrumentation + cursor API (tracked as a follow-up).
              The helper functions startStepThrough/executeStep/
              stopStepThrough remain defined to keep keyboard shortcut
              wiring inert, but are no longer reachable from the UI. */}
        </div>
      </div>

      {/* Purpose bar (collapsible) */}
      {wfPurpose && (
        <div className="border-b bg-muted/20 px-4 py-1.5 flex items-center gap-3">
          <Brain className="h-3.5 w-3.5 text-primary shrink-0" />
          <p className="text-[10px] text-muted-foreground italic truncate flex-1">"{wfPurpose}"</p>
          <Badge variant="outline" className="text-[8px] shrink-0">{wfCriteria.length} criteria</Badge>
          <Badge variant="outline" className="text-[8px] shrink-0">{wfConstraints.length} constraints</Badge>
          <Button variant="ghost" size="sm" className="h-5 text-[9px] px-1.5 shrink-0" onClick={() => { setSelectedNodeId(null); setRightPanelMode('supervisor'); }}>
            Edit
          </Button>
        </div>
      )}

      {/* 3-panel layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: Node Palette */}
        <NodePalette onAdd={handlePaletteAdd} />

        {/* Center: React Flow Canvas */}
        <div className="flex-1 relative" ref={reactFlowWrapper} style={{ background: 'hsl(var(--background))' }}>
          <ReactFlow
            nodes={nodes}
            className="[&_.react-flow__edge-path]:!stroke-muted-foreground/30"
            edges={edges}
            onNodesChange={handleNodesChange}
            onEdgesChange={handleEdgesChange}
            onConnect={onConnect}
            isValidConnection={isValidConnection}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            onDragOver={onDragOver}
            onDrop={onDrop}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.3 }}
            snapToGrid
            snapGrid={[15, 15]}
            selectionOnDrag
            selectionMode={SelectionMode.Partial}
            multiSelectionKeyCode="Shift"
            defaultEdgeOptions={{
              type: 'default',
              animated: false,
              style: { strokeWidth: 1.5, stroke: 'hsl(var(--muted-foreground) / 0.3)' },
            }}
            proOptions={{ hideAttribution: true }}
          >
            <Controls
              position="top-left"
              className="!bg-card !border-border !shadow-md [&>button]:!bg-card [&>button]:!border-border [&>button]:!text-foreground [&>button:hover]:!bg-muted"
              style={{ borderRadius: 8 }}
            />
            {showMinimap && (
              <MiniMap
                position="bottom-right"
                pannable
                zoomable
                nodeStrokeColor={isDark ? '#a78bfa' : '#7c3aed'}
                nodeColor={isDark ? '#374151' : '#e5e7eb'}
                maskColor={isDark ? 'rgba(9,9,11,0.85)' : 'rgba(255,255,255,0.85)'}
                style={{
                  borderRadius: 8,
                  backgroundColor: isDark ? '#18181b' : '#ffffff',
                  border: isDark ? '1px solid #27272a' : '1px solid #e5e7eb',
                }}
              />
            )}
            <Background
              variant={BackgroundVariant.Dots}
              gap={15}
              size={1}
              color="hsl(var(--muted-foreground) / 0.15)"
            />

            {/* Empty state */}
            {nodes.length === 0 && (
              <Panel position="top-center">
                <div className="bg-background/95 border rounded-xl px-8 py-6 text-center mt-24 shadow-sm">
                  <Workflow className="h-10 w-10 mx-auto mb-3 text-primary/30" />
                  <p className="text-sm font-semibold">Start building your workflow</p>
                  <p className="text-xs text-muted-foreground mt-1 max-w-[280px]">
                    Drag nodes from the palette on the left onto this canvas, then connect them to build your automation pipeline.
                  </p>
                </div>
              </Panel>
            )}

            {/* Multi-select badge */}
            {selectedNodeIds.length > 1 && (
              <Panel position="top-center">
                <div className="flex items-center gap-2 bg-primary/10 border border-primary/20 text-primary rounded-lg px-3 py-1.5 text-xs font-medium mt-2">
                  {selectedNodeIds.length} nodes selected
                  <span className="text-[10px] text-primary/60 ml-1">
                    Del to delete / Ctrl+C to copy
                  </span>
                </div>
              </Panel>
            )}

            {/* Test run overlay indicator.
                #197 / #205 — surface real progress (steps walked +
                execution id) so the operator can see the run is
                actually happening AND has a handle to find it in
                History. Without this the canvas just sat with an
                idle "in progress..." spinner and operators concluded
                the run never started. */}
            {testRunning && (
              <Panel position="top-right">
                <div className="flex flex-col gap-1 bg-blue-500/10 border border-blue-500/30 text-blue-700 dark:text-blue-300 rounded-lg px-3 py-2 text-xs font-medium max-w-[280px]">
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Test execution in progress
                  </div>
                  {executionDetail && executionDetail.steps.length > 0 && (
                    <p className="text-[10px] text-blue-700/80 dark:text-blue-300/80 font-normal">
                      {executionDetail.steps.filter((s) => s.status === 'success').length}
                      {' / '}
                      {executionDetail.steps.length} steps completed
                    </p>
                  )}
                  {activeExecutionId && (
                    <p
                      className="text-[9px] text-blue-700/60 dark:text-blue-300/60 font-mono truncate"
                      title={activeExecutionId}
                    >
                      run: {activeExecutionId.slice(0, 8)}
                    </p>
                  )}
                </div>
              </Panel>
            )}
          </ReactFlow>
        </div>

        {/* Right Panel: Scorecard (after test run) OR Properties (node selected) OR Supervisor (default) */}
        {scorecardData ? (
          <QualityScorecard
            data={scorecardData}
            executionId={scorecardExecId}
            onOpenHistory={(execId) =>
              navigate({
                to: '/automate/history/$executionId',
                params: { executionId: execId },
              } as never)
            }
            onClose={() => setScorecardData(null)}
          />
        ) : selectedNode && rightPanelMode === 'properties' ? (
          <NodePropertyPanel
            nodeId={selectedNode.id}
            nodeType={selectedNode.type as NodeType}
            label={(selectedNode.data as any).label ?? 'Unnamed'}
            config={(selectedNode.data as any).config ?? {}}
            onUpdate={handleNodeUpdate}
            onDelete={handleNodeDelete}
            onClose={() => { setSelectedNodeId(null); setRightPanelMode('supervisor'); }}
          />
        ) : (
          <SupervisorPanel
            purpose={wfPurpose}
            onPurposeChange={setWfPurpose}
            criteria={wfCriteria}
            onCriteriaChange={setWfCriteria}
            constraints={wfConstraints}
            onConstraintsChange={setWfConstraints}
            supervisorMode={wfSupervisorMode}
            onSupervisorModeChange={setWfSupervisorMode}
            workflowId={isLiveWorkflow ? id : undefined}
          />
        )}
      </div>

      {/* Agent Config Sheet (opens when clicking an Agent node) */}
      {agentSheetNodeId && (() => {
        const agentNode = nodes.find(n => n.id === agentSheetNodeId);
        if (!agentNode) return null;
        return (
          <AgentConfigSheet
            open={!!agentSheetNodeId}
            onOpenChange={open => { if (!open) setAgentSheetNodeId(null); }}
            config={(agentNode.data as any).config ?? {}}
            label={(agentNode.data as any).label ?? 'Agent'}
            onSave={(newConfig, newLabel) => {
              setNodes(nds => {
                const updated = nds.map(n => n.id === agentSheetNodeId ? { ...n, data: { ...n.data, label: newLabel, config: newConfig } } : n);
                setEdges((currentEdges) => {
                  pushHistory(updated, currentEdges);
                  return currentEdges;
                });
                return updated;
              });
              markDirty();
              setAgentSheetNodeId(null);
            }}
          />
        );
      })()}

      {/* Keyboard Shortcuts Dialog */}
      <Dialog open={showShortcuts} onOpenChange={setShowShortcuts}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Keyboard className="h-4 w-4" /> Keyboard Shortcuts</DialogTitle></DialogHeader>
          <div className="space-y-3 text-xs">
            {[
              { keys: 'Ctrl + Z', action: 'Undo' },
              { keys: 'Ctrl + Y', action: 'Redo' },
              { keys: 'Ctrl + S', action: 'Save workflow' },
              { keys: 'Ctrl + C', action: 'Copy selected nodes' },
              { keys: 'Ctrl + V', action: 'Paste copied nodes' },
              { keys: 'Ctrl + A', action: 'Select all nodes' },
              { keys: 'Delete / Backspace', action: 'Delete selected nodes' },
              { keys: 'Escape', action: 'Deselect all / close panel' },
              { keys: 'Space + Drag', action: 'Pan canvas' },
              { keys: 'Scroll', action: 'Zoom in/out' },
              { keys: '?', action: 'Show this dialog' },
            ].map(s => (
              <div key={s.keys} className="flex items-center justify-between">
                <span className="text-muted-foreground">{s.action}</span>
                <kbd className="px-2 py-0.5 bg-muted rounded text-[10px] font-mono">{s.keys}</kbd>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
